import { useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { CopPoint } from './heatmapFunctions';

interface Props {
  leftHistory: (CopPoint | null)[];
  rightHistory: (CopPoint | null)[];
  centerHistory: (CopPoint | null)[];
  currentIndex: number;
  width: number;
  height: number;
  showTrace?: boolean;
  // Per-sample timestamps in ms, aligned to the history indices. When present
  // the trace length is governed by TRAIL_DURATION_MS rather than sample count.
  timestamps?: number[];
}

type ScreenPt = { x: number; y: number };

// Heatmap internal coordinate space — matches the 300×300 unit space used in
// heatmapFunctions.ts for sensor locations and COP calculations.
const HEATMAP_SPACE = 300;

// Y correction that aligns dashboard heatmap-space coordinates with the mobile
// app's screen-pixel positions. Derived from:
//   mobile uses topOffset = -size/17
//   dashboard sensor locations use -(heatmapHeight - 250)/2 = -25
//   difference in heatmap-space units = 25 - (300/17) = 125/17
const COP_Y_SHIFT = 125 / 17;

// TUNABLE: how far back in time the COP trace extends from the current frame,
// in milliseconds. This is the single knob for the trail length — raise it for
// a longer trail, lower it for a shorter, snappier one. A full gait cycle (one
// complete butterfly / figure-of-8 of the centre-of-pressure) is ~1.1s at
// normal cadence, so ~1000ms shows about one butterfly while still visibly
// trailing (the tail drops off as playback advances).
const TRAIL_DURATION_MS = 900;

// Hard safety cap on trace length in samples, applied when per-sample
// timestamps aren't supplied (e.g. in tests). Prevents an unbounded path; with
// timestamps the duration above governs instead.
const TRAIL_MAX_LENGTH = 300;

// The visible COP trace for one foot: the contiguous run of non-null samples
// ending at the current frame, limited to the most recent TRAIL_DURATION_MS.
// A null sample means zero pressure (foot in the air), so the line clears on
// foot-off and starts fresh on the next contact — a trail/snake matching the
// mobile app's behaviour. Returns an empty array when the current frame itself
// has no contact.
const currentTrail = (
  history: (CopPoint | null)[],
  currentIndex: number,
  timestamps?: number[],
): (CopPoint | null)[] => {
  if (currentIndex < 0 || currentIndex >= history.length) return [];
  if (history[currentIndex] === null) return [];

  const currentTs = timestamps?.[currentIndex];
  let start = currentIndex;
  while (start > 0 && history[start - 1] !== null) {
    if (currentIndex - start + 1 >= TRAIL_MAX_LENGTH) break;
    const prevTs = timestamps?.[start - 1];
    if (
      currentTs !== undefined &&
      prevTs !== undefined &&
      currentTs - prevTs > TRAIL_DURATION_MS
    ) {
      break;
    }
    start--;
  }
  return history.slice(start, currentIndex + 1);
};

const toScreen = (p: CopPoint, scale: number): ScreenPt => ({
  x: scale * p.x,
  y: scale * (p.y + COP_Y_SHIFT),
});

const buildCatmullRomPath = (
  history: (CopPoint | null)[],
  scale: number,
): string => {
  const pts = history
    .filter((p): p is CopPoint => p !== null)
    .map((p) => toScreen(p, scale));
  if (pts.length < 2) return '';

  const tension = 0.3;
  let d = `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;

  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];

    const cp1x = p1.x + ((p2.x - p0.x) * tension) / 3;
    const cp1y = p1.y + ((p2.y - p0.y) * tension) / 3;
    const cp2x = p2.x - ((p3.x - p1.x) * tension) / 3;
    const cp2y = p2.y - ((p3.y - p1.y) * tension) / 3;

    d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }
  return d;
};

const CopPathOverlay: React.FC<Props> = ({
  leftHistory,
  rightHistory,
  centerHistory,
  currentIndex,
  width,
  height,
  showTrace = false,
  timestamps,
}) => {
  // Converts one unit of heatmap coordinate space into screen pixels.
  // The heatmap is always square, so a single value covers both axes.
  // Any new overlay element added here should use this scale rather than
  // re-deriving the ratio from width/height directly.
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const scale = width / HEATMAP_SPACE;

  const leftPath = useMemo(
    () => buildCatmullRomPath(currentTrail(leftHistory, currentIndex, timestamps), scale),
    [leftHistory, currentIndex, timestamps, scale]
  );
  const rightPath = useMemo(
    () => buildCatmullRomPath(currentTrail(rightHistory, currentIndex, timestamps), scale),
    [rightHistory, currentIndex, timestamps, scale]
  );
  const centerPath = useMemo(
    () => buildCatmullRomPath(currentTrail(centerHistory, currentIndex, timestamps), scale),
    [centerHistory, currentIndex, timestamps, scale]
  );

  const leftDot = leftHistory[currentIndex]
    ? toScreen(leftHistory[currentIndex]!, scale)
    : null;
  const rightDot = rightHistory[currentIndex]
    ? toScreen(rightHistory[currentIndex]!, scale)
    : null;
  const centerDot = centerHistory[currentIndex]
    ? toScreen(centerHistory[currentIndex]!, scale)
    : null;

  return (
    <svg
      width={width}
      height={height}
      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
    >
      {/* Center reference line — matches centerLine in mobile copOverlay styles */}
      <line x1={width / 2} y1={0} x2={width / 2} y2={height} stroke="rgba(0,0,0,0.3)" strokeWidth={1} />

      {showTrace && leftPath && (
        <path d={leftPath} fill="none" stroke={primaryColor} strokeWidth={1.5} opacity={0.6} />
      )}
      {showTrace && rightPath && (
        <path d={rightPath} fill="none" stroke={primaryColor} strokeWidth={1.5} opacity={0.6} />
      )}
      {showTrace && centerPath && (
        <path d={centerPath} fill="none" stroke="rgba(0,0,0,0.4)" strokeWidth={1} />
      )}

      {leftDot && <circle cx={leftDot.x} cy={leftDot.y} r={4} fill={primaryColor} />}
      {rightDot && <circle cx={rightDot.x} cy={rightDot.y} r={4} fill={primaryColor} />}
      {/* Center dot: white fill with border — matches centerDot style in mobile app */}
      {centerDot && <circle cx={centerDot.x} cy={centerDot.y} r={4} fill="white" stroke={primaryColor} strokeWidth={2} />}
    </svg>
  );
};

export default CopPathOverlay;
